// shadcn-style Button — 7shifts design language.
// Все кнопки pill-shaped (rounded-full), кроме ghost (rounded-[20px] per spec).
// Никаких shadow на любом состоянии.
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  // base: Inter Medium, transition, focus ring (Blueprint Blue), disabled state.
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium tracking-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary CTA — Blueprint Blue, pill.
        default: 'rounded-full bg-primary text-primary-foreground hover:opacity-90',
        // Destructive — Soft Orange Highlight (используется редко).
        destructive: 'rounded-full bg-destructive text-canvas-white hover:opacity-90',
        // Outline — Canvas White с Divider Gray border, pill.
        outline: 'rounded-full border border-input bg-canvas-white text-foreground hover:bg-muted',
        // Secondary — Whisper White, pill.
        secondary:
          'rounded-full bg-whisper-white text-ink-black border border-divider-gray hover:opacity-90',
        // Ghost — для навигации. Per spec: border-radius 20px (НЕ pill).
        ghost: 'rounded-[20px] bg-transparent text-foreground hover:bg-muted',
        // Link — Spring Green emphasis.
        link: 'text-primary underline-offset-4 hover:underline rounded-none',
      },
      size: {
        // max-lg — тач-таргеты: 44px по рекомендации Apple/Google. Кнопка в
        // 32px на телефоне промахивается через раз (план 110).
        sm: 'h-8 px-3 text-xs max-lg:h-10 max-lg:px-4 max-lg:text-sm',
        md: 'h-10 px-4 text-sm max-lg:h-11',
        default: 'h-10 px-4 text-sm max-lg:h-11',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10 rounded-full p-0 max-lg:h-11 max-lg:w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);

Button.displayName = 'Button';
